import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import {
  USER_SCROLL_UP_DISENGAGE_PX,
  absorbScrollIntoSpacer,
  calculateStreamingPinSpacerHeight,
  getContentBottomScrollTop,
  getScrollTopToAlignElementTop,
  hasAssistantReplyContentStarted,
  hasContentOutOfView as measureHasContentOutOfView,
  isNearContentBottom,
  shrinkSpacerByContentGrowth,
  type ScrollMetrics,
} from './chatScrollGeometry'

/** Container-relative scrollTop for a message node (safe with nested list wrappers). */
function getScrollTopForMessageEl(wrapper: HTMLElement, target: HTMLElement): number {
  const wrapperRect = wrapper.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  return getScrollTopToAlignElementTop({
    containerScrollTop: wrapper.scrollTop,
    containerTop: wrapperRect.top,
    elementTop: targetRect.top,
  })
}

/** Escape message ids before building `[data-message-id="…"]` selectors (CSS.escape). */
function getMessageSelector(messageId: string): string {
  const escapedMessageId = typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(messageId)
    : messageId.replace(/["\\]/g, '\\$&')
  return `[data-message-id="${escapedMessageId}"]`
}

function qsById(wrapper: HTMLElement, messageId: string): HTMLElement | null {
  return wrapper.querySelector<HTMLElement>(getMessageSelector(messageId))
}

export type ChatSnapPoint = 'collapsed' | 'expanded'

const SCROLL_FLAG_RESET_DELAY_MS = 500
const MAX_SCROLL_RETRIES = 3
const MAX_ELEMENT_FIND_RETRIES = 5
const FALLBACK_SPACER_RATIO = 0.6
const SPACER_RECALCULATE_DELAY_MS = 100
const WRAPPER_VERTICAL_PADDING = 44
const LOADING_INDICATOR_HEIGHT = 64
const WHEEL_GESTURE_IDLE_MS = 120

/**
 * ChatGPT-style scroll contract (owned here — not by page containers):
 *
 * 1. Send → smoothly slide the user message near the top once, with a temporary trailing spacer.
 * 2. Streaming + follow → keep pin blank through the Thinking/loader phase; shrink spacer
 *    by the same delta the reply grows (keeps scrollHeight stable → bubble stays glued
 *    without scrollTop writes). Never re-snap scrollTop from layout or the scroll
 *    handler while gesturing (both caused flicker / mobile lock).
 *    Keep the pin active after blank runs out so long answers do not move the reader.
 * 3. Scroll up into history → collapse spacer / stop follow.
 * 4. Scroll down through the trailing blank → absorb scroll into spacer progressively
 *    (shrink by scroll amount); fully clear near the end of the blank (ChatGPT).
 * 5. Return near the bottom → release the pin and resume following the tail.
 * 6. Stream end → remove any remaining temporary blank.
 * 7. Regenerate → use the same one-shot turn pin as send.
 *    Jump-to-latest explicitly releases the pin and opts into following the tail.
 */

const UNREAD_STORAGE_KEY = 'tripPlanner.chatLastReadMessage'

function loadUnreadMap(): Map<string, string | null> {
  if (typeof window === 'undefined') {
    return new Map()
  }
  try {
    const stored = window.localStorage.getItem(UNREAD_STORAGE_KEY)
    if (!stored) {
      return new Map()
    }
    const parsed = JSON.parse(stored)
    if (typeof parsed !== 'object' || parsed === null) {
      return new Map()
    }
    return new Map(Object.entries(parsed))
  } catch {
    return new Map()
  }
}

let saveDebounceTimer: ReturnType<typeof setTimeout> | undefined = undefined

function saveUnreadMap(map: Map<string, string | null>): void {
  if (typeof window === 'undefined') {
    return
  }

  if (saveDebounceTimer) {
    clearTimeout(saveDebounceTimer)
  }
  saveDebounceTimer = setTimeout(() => {
    try {
      const obj: Record<string, string | null> = {}
      map.forEach((value, key) => {
        obj[key] = value
      })
      window.localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify(obj))
    } catch {}
  }, 500)
}

interface UseChatScrollArgs {
  messagesWrapperRef: React.MutableRefObject<HTMLDivElement | null>
  isStreaming: boolean
  messages: Array<MessageItem>
  chatKey: string
  chatSnapPoint: ChatSnapPoint
  shouldUseMobileOverlay: boolean
  isViewingHistory: boolean
  hasMessages: boolean
  activeHistoryIndex: number | undefined
}

interface UseChatScrollReturn {
  handleScroll: () => void
  scrollToBottom: (force?: boolean) => void
  isAtBottom: boolean
  followTail: boolean
  hasContentOutOfView: boolean
  enableFollowAndScrollToBottom: () => void
  setFollowTail: React.Dispatch<React.SetStateAction<boolean>>
  scrollToMessage: (messageId: string | undefined, behavior?: ScrollBehavior) => boolean
  calculateSpacerHeight: () => number
  dynamicSpacerHeight: number
  setDynamicSpacerHeight: React.Dispatch<React.SetStateAction<number>>
  scrollToNewMessage: (messageId: string) => void
  recalculateSpacerAfterStream: () => void
}

function readScrollMetrics(wrapper: HTMLDivElement, spacerHeight: number): ScrollMetrics {
  return {
    scrollTop: wrapper.scrollTop,
    clientHeight: wrapper.clientHeight,
    scrollHeight: wrapper.scrollHeight,
    spacerHeight,
  }
}

export function useChatScroll(args: UseChatScrollArgs): UseChatScrollReturn {
  const {
    messagesWrapperRef,
    isStreaming,
    messages,
    chatKey,
    chatSnapPoint,
    shouldUseMobileOverlay,
    isViewingHistory,
    hasMessages,
    activeHistoryIndex,
  } = args

  const [isAtBottom, setIsAtBottom] = useState(true)
  const [followTail, setFollowTail] = useState(false)
  const [hasContentOutOfView, setHasContentOutOfView] = useState(false)
  const [dynamicSpacerHeight, setDynamicSpacerHeight] = useState(0)

  const lastScrollTopRef = useRef(0)
  const isSmoothScrollingRef = useRef(false)
  const isProgrammaticScrollRef = useRef(false)
  const programmaticScrollGenerationRef = useRef(0)
  /** True only while a real wheel/touch gesture is in progress. */
  const isUserGesturingRef = useRef(false)
  /** True after send until the user clears the pin / leftover blank. */
  const isPinSessionActiveRef = useRef(false)
  /**
   * When true, ignore gesture disengage while a spacer-absorb camera correction
   * is in flight. Send-time smooth scroll does not set this, so the user can still
   * cancel or scroll away immediately.
   */
  const suppressDisengageForProgrammaticRef = useRef(false)
  const followTailRef = useRef(followTail)
  const isStreamingRef = useRef(isStreaming)
  const wasStreamingRef = useRef(isStreaming)
  const dynamicSpacerHeightRef = useRef(dynamicSpacerHeight)
  /**
   * High-water mark of the pinned reply's measured height. Spacer shrinks only by
   * positive growth deltas from this mark — never by absolute remeasure (avoids
   * over-shrink → browser scrollTop clamp → drift, and avoids re-anchor flicker).
   */
  const pinnedReplyContentHeightRef = useRef(0)
  const lastReadMessageByChatRef = useRef<Map<string, string | null>>(loadUnreadMap())
  const previousChatSnapPointRef = useRef<ChatSnapPoint>(chatSnapPoint)
  const wheelGestureTimeoutRef = useRef<number | undefined>(undefined)
  const touchStartYRef = useRef<number | undefined>(undefined)

  followTailRef.current = followTail
  isStreamingRef.current = isStreaming
  dynamicSpacerHeightRef.current = dynamicSpacerHeight

  const isCollapsedMobile = shouldUseMobileOverlay && chatSnapPoint === 'collapsed'

  const beginProgrammaticScroll = useCallback(() => {
    programmaticScrollGenerationRef.current += 1
    isProgrammaticScrollRef.current = true
    return programmaticScrollGenerationRef.current
  }, [])

  const endProgrammaticScroll = useCallback((generation: number, delayMs = 0) => {
    const clearProgrammatic = () => {
      if (programmaticScrollGenerationRef.current === generation) {
        isProgrammaticScrollRef.current = false
        isSmoothScrollingRef.current = false
      }
    }
    // Always release disengage-suppress quickly. Smooth programmatic scrolling keeps
    // isProgrammaticScroll for the animation window, while still allowing the user
    // to scroll away during that window.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (programmaticScrollGenerationRef.current === generation) {
          suppressDisengageForProgrammaticRef.current = false
        }
      })
    })
    if (delayMs > 0) {
      window.setTimeout(clearProgrammatic, delayMs)
      return
    }
    // Double rAF lets the browser deliver scroll events from this write first.
    requestAnimationFrame(() => {
      requestAnimationFrame(clearProgrammatic)
    })
  }, [])

  const getSpacerHeightFromDom = useCallback((wrapper: HTMLDivElement) => {
    const spacer = wrapper.querySelector<HTMLElement>('[data-spacer]')
    return spacer?.offsetHeight ?? dynamicSpacerHeightRef.current
  }, [])

  const cancelSmoothScroll = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (wrapper && isSmoothScrollingRef.current) {
      const generation = beginProgrammaticScroll()
      wrapper.scrollTo({
        top: wrapper.scrollTop,
        behavior: 'auto',
      })
      isSmoothScrollingRef.current = false
      endProgrammaticScroll(generation)
    }
  }, [beginProgrammaticScroll, endProgrammaticScroll, messagesWrapperRef])

  /**
   * Instant stick to real content bottom. Skips while the user is gesturing so we
   * never fight touch/wheel (the failure mode of the previous pin lock).
   */
  const stickToContentBottom = useCallback(() => {
    if (isUserGesturingRef.current) {
      return
    }
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }

    const metrics = readScrollMetrics(wrapper, getSpacerHeightFromDom(wrapper))
    const targetScrollTop = getContentBottomScrollTop(metrics)
    if (Math.abs(wrapper.scrollTop - targetScrollTop) <= 1) {
      lastScrollTopRef.current = wrapper.scrollTop
      return
    }

    const generation = beginProgrammaticScroll()
    wrapper.scrollTop = targetScrollTop
    lastScrollTopRef.current = targetScrollTop
    endProgrammaticScroll(generation)
  }, [beginProgrammaticScroll, endProgrammaticScroll, getSpacerHeightFromDom, messagesWrapperRef])

  const collapsePinSession = useCallback(() => {
    isPinSessionActiveRef.current = false
    pinnedReplyContentHeightRef.current = 0
    dynamicSpacerHeightRef.current = 0
    setDynamicSpacerHeight(0)
  }, [])

  const resumeTailFollow = useCallback(() => {
    collapsePinSession()
    followTailRef.current = true
    setFollowTail(true)
  }, [collapsePinSession])

  const disengageFollow = useCallback(() => {
    followTailRef.current = false
    setFollowTail(false)
    if (isPinSessionActiveRef.current) {
      collapsePinSession()
    }
  }, [collapsePinSession])

  const scrollToBottom = useCallback((force = false) => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }
    if (!force && !followTailRef.current) {
      return
    }
    if (!force && isUserGesturingRef.current) {
      return
    }

    const metrics = readScrollMetrics(wrapper, getSpacerHeightFromDom(wrapper))
    const targetScrollTop = getContentBottomScrollTop(metrics)
    const generation = beginProgrammaticScroll()
    isSmoothScrollingRef.current = true
    wrapper.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    })
    endProgrammaticScroll(generation, SCROLL_FLAG_RESET_DELAY_MS)
  }, [beginProgrammaticScroll, endProgrammaticScroll, getSpacerHeightFromDom, messagesWrapperRef])

  const checkIsNearBottom = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return false
    }
    return isNearContentBottom(readScrollMetrics(wrapper, dynamicSpacerHeightRef.current))
  }, [messagesWrapperRef])

  const checkHasContentOutOfView = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return false
    }
    return measureHasContentOutOfView(readScrollMetrics(wrapper, dynamicSpacerHeightRef.current))
  }, [messagesWrapperRef])

  const getLastReadMessageId = useCallback(() => (
    lastReadMessageByChatRef.current.get(chatKey) ?? null
  ), [chatKey])

  const setLastReadMessageId = useCallback((messageId: string | null) => {
    lastReadMessageByChatRef.current.set(chatKey, messageId)
    saveUnreadMap(lastReadMessageByChatRef.current)
  }, [chatKey])

  const markLatestMessageAsRead = useCallback(() => {
    const latestMessageId = messages[messages.length - 1]?.id
    if (latestMessageId) {
      setLastReadMessageId(latestMessageId)
    }
  }, [messages, setLastReadMessageId])

  const scrollToMessage = useCallback((
    messageId: string | undefined,
    behavior: ScrollBehavior = 'smooth',
  ) => {
    if (!messageId) {
      return false
    }
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return false
    }
    const target = qsById(wrapper, messageId)
    if (!target) {
      return false
    }

    const scrollTarget = getScrollTopForMessageEl(wrapper, target)
    const generation = beginProgrammaticScroll()
    isSmoothScrollingRef.current = behavior === 'smooth'
    setIsAtBottom(false)
    wrapper.scrollTo({
      top: scrollTarget,
      behavior,
    })
    lastScrollTopRef.current = scrollTarget
    endProgrammaticScroll(
      generation,
      behavior === 'smooth' ? SCROLL_FLAG_RESET_DELAY_MS : 0,
    )
    return true
  }, [beginProgrammaticScroll, endProgrammaticScroll, messagesWrapperRef])

  /** Exposed for callers; idle spacer is always 0 outside an active pin session. */
  const calculateSpacerHeight = useCallback(() => 0, [])

  /** Measured height of the pinned turn's assistant reply (0 until real content starts). */
  const measurePinnedReplyContentHeight = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return undefined
    }

    const lastUserIndex = messages.findLastIndex(m => m.role === 'user')
    if (lastUserIndex < 0) {
      return undefined
    }

    const replyAssistant = messages
      .slice(lastUserIndex + 1)
      .findLast(m => m.role === 'assistant')

    if (!replyAssistant || !hasAssistantReplyContentStarted(replyAssistant)) {
      return 0
    }

    const replyEl = qsById(wrapper, replyAssistant.id)
    return replyEl?.offsetHeight || 0
  }, [messages, messagesWrapperRef])

  const recomputeActivePinSpacer = useCallback(() => {
    // Shrink while streaming; any remaining blank is removed when the stream ends.
    if (!isPinSessionActiveRef.current || !followTailRef.current || !isStreamingRef.current) {
      return
    }

    const contentHeight = measurePinnedReplyContentHeight()
    if (contentHeight === undefined) {
      return
    }

    const growthDelta = contentHeight - pinnedReplyContentHeightRef.current
    if (growthDelta <= 0) {
      return
    }

    pinnedReplyContentHeightRef.current = contentHeight
    const previousSpacerHeight = dynamicSpacerHeightRef.current
    const newSpacerHeight = shrinkSpacerByContentGrowth(
      previousSpacerHeight,
      growthDelta,
    )
    setDynamicSpacerHeight(newSpacerHeight)
    dynamicSpacerHeightRef.current = newSpacerHeight
    // Exhausting the blank must not opt the reader into following the tail.
    // Keep the session active so further content grows below their reading position.
  }, [measurePinnedReplyContentHeight])

  const resolveUnreadAnchorId = useCallback(() => {
    const latestMessageId = messages[messages.length - 1]?.id
    if (!latestMessageId) {
      return { hasUnread: false, targetId: null, latestMessageId: null }
    }

    const lastReadMessageId = getLastReadMessageId()
    const hasUnread = !lastReadMessageId || lastReadMessageId !== latestMessageId

    if (!hasUnread) {
      return { hasUnread: false, targetId: null, latestMessageId }
    }

    const lastReadIndex = lastReadMessageId
      ? messages.findIndex(message => message.id === lastReadMessageId)
      : -1
    const lastUnreadMessage = lastReadIndex >= 0
      ? messages.slice(lastReadIndex + 1).pop()
      : messages[messages.length - 1]

    if (!lastUnreadMessage) {
      return { hasUnread: false, targetId: null, latestMessageId }
    }

    const lastUnreadIndex = messages.findIndex(message => message.id === lastUnreadMessage.id)
    if (lastUnreadIndex <= 0) {
      return { hasUnread, targetId: lastUnreadMessage.id, latestMessageId }
    }

    const previousUserMessage = [...messages]
      .slice(0, lastUnreadIndex)
      .reverse()
      .find(message => message.role === 'user')

    return {
      hasUnread,
      targetId: previousUserMessage?.id ?? lastUnreadMessage.id,
      latestMessageId,
    }
  }, [getLastReadMessageId, messages])

  const handleScroll = useCallback(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }

    const currentScrollTop = wrapper.scrollTop
    const previousScrollTop = lastScrollTopRef.current
    const scrollDelta = currentScrollTop - previousScrollTop
    const isScrollingUp = scrollDelta < 0
    const isScrollingDown = scrollDelta > 0
    const isProgrammaticScroll = isProgrammaticScrollRef.current

    if (isScrollingUp && isSmoothScrollingRef.current && !isProgrammaticScroll) {
      cancelSmoothScroll()
    }

    lastScrollTopRef.current = currentScrollTop

    const nearBottom = checkIsNearBottom()
    const contentOutOfView = checkHasContentOutOfView()
    setIsAtBottom(nearBottom)

    // Never continuously re-lock scrollTop to "maintain" a pin (mobile lock regression).
    // The only intentional scrollTop write here is ChatGPT blank-absorb: convert a
    // downward nudge through the trailing spacer into spacer shrinkage.
    // Real gestures can still cancel send-time smooth scroll. Spacer absorption
    // suppresses disengage while its camera correction settles.
    if (isUserGesturingRef.current) {
      const blockDisengage = isProgrammaticScroll && suppressDisengageForProgrammaticRef.current
      const spacerHeight = dynamicSpacerHeightRef.current
      const pinActive = isPinSessionActiveRef.current || spacerHeight > 0
      let consumedPinThisEvent = false

      if (!blockDisengage) {
        if (pinActive && isScrollingDown && scrollDelta > 0) {
          const { nextSpacerHeight, absorbedDelta, didFullyClear } = absorbScrollIntoSpacer({
            spacerHeight,
            scrollDelta,
          })

          if (absorbedDelta > 0) {
            if (didFullyClear) {
              disengageFollow()
            } else {
              setDynamicSpacerHeight(nextSpacerHeight)
              dynamicSpacerHeightRef.current = nextSpacerHeight
              // Absorb: keep the pin resting position while the blank shrinks.
              // Suppress disengage — rewriting scrollTop fires an upward scroll
              // event that would otherwise wipe the entire spacer (localhost bug).
              suppressDisengageForProgrammaticRef.current = true
              const generation = beginProgrammaticScroll()
              wrapper.scrollTop = previousScrollTop
              lastScrollTopRef.current = previousScrollTop
              endProgrammaticScroll(generation)
            }
            consumedPinThisEvent = true
          }
        } else if (isScrollingUp && -scrollDelta >= USER_SCROLL_UP_DISENGAGE_PX) {
          // History scroll clears any leftover / pin space in one go.
          disengageFollow()
          consumedPinThisEvent = true
        }
      }

      if (!consumedPinThisEvent && !isProgrammaticScroll && isScrollingDown && nearBottom && !followTailRef.current) {
        resumeTailFollow()
      }
    }

    if (isViewingHistory || isCollapsedMobile || isSmoothScrollingRef.current) {
      setHasContentOutOfView(false)
    } else {
      setHasContentOutOfView(contentOutOfView)
    }

    if (nearBottom) {
      markLatestMessageAsRead()
    }
  }, [
    beginProgrammaticScroll,
    cancelSmoothScroll,
    checkHasContentOutOfView,
    checkIsNearBottom,
    disengageFollow,
    endProgrammaticScroll,
    isCollapsedMobile,
    isViewingHistory,
    markLatestMessageAsRead,
    messagesWrapperRef,
    resumeTailFollow,
  ])

  const enableFollowAndScrollToBottom = useCallback(() => {
    setIsAtBottom(true)
    setHasContentOutOfView(false)

    // Explicitly jumping to latest opts into following new content at the bottom.
    resumeTailFollow()

    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }

    const metrics = readScrollMetrics(wrapper, 0)
    const targetScrollTop = getContentBottomScrollTop({
      ...metrics,
      spacerHeight: 0,
    })
    const generation = beginProgrammaticScroll()
    isSmoothScrollingRef.current = true
    wrapper.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    })
    endProgrammaticScroll(generation, SCROLL_FLAG_RESET_DELAY_MS)
  }, [beginProgrammaticScroll, endProgrammaticScroll, messagesWrapperRef, resumeTailFollow])

  // Capture real gestures separately from layout-driven scrollTop changes.
  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) {
      return
    }

    const markGesturing = () => {
      isUserGesturingRef.current = true
    }

    const clearGesturing = () => {
      isUserGesturingRef.current = false
      // A reply can grow during the wheel/touch cooldown. Its resize callback
      // intentionally skips camera writes while the gesture is active, so catch
      // up once the user has finished returning to the bottom.
      if (followTailRef.current && isStreamingRef.current && !isPinSessionActiveRef.current) {
        stickToContentBottom()
      }
    }

    const onWheel = (event: WheelEvent) => {
      markGesturing()
      if (event.deltaY < 0) {
        cancelSmoothScroll()
        disengageFollow()
      }
      window.clearTimeout(wheelGestureTimeoutRef.current)
      wheelGestureTimeoutRef.current = window.setTimeout(clearGesturing, WHEEL_GESTURE_IDLE_MS)
    }

    const onTouchStart = (event: TouchEvent) => {
      markGesturing()
      touchStartYRef.current = event.touches[0]?.clientY
    }

    const onTouchMove = (event: TouchEvent) => {
      const touchY = event.touches[0]?.clientY
      if (touchY !== undefined && touchStartYRef.current !== undefined && touchY - touchStartYRef.current > 4) {
        cancelSmoothScroll()
        disengageFollow()
      }
    }

    const onTouchEnd = () => {
      touchStartYRef.current = undefined
      // Defer one frame so the final scroll event still sees the gesture flag.
      requestAnimationFrame(clearGesturing)
    }

    wrapper.addEventListener('wheel', onWheel, { passive: true })
    wrapper.addEventListener('touchstart', onTouchStart, { passive: true })
    wrapper.addEventListener('touchmove', onTouchMove, { passive: true })
    wrapper.addEventListener('touchend', onTouchEnd, { passive: true })
    wrapper.addEventListener('touchcancel', onTouchEnd, { passive: true })

    return () => {
      wrapper.removeEventListener('wheel', onWheel)
      wrapper.removeEventListener('touchstart', onTouchStart)
      wrapper.removeEventListener('touchmove', onTouchMove)
      wrapper.removeEventListener('touchend', onTouchEnd)
      wrapper.removeEventListener('touchcancel', onTouchEnd)
      window.clearTimeout(wheelGestureTimeoutRef.current)
    }
  }, [cancelSmoothScroll, disengageFollow, messagesWrapperRef, stickToContentBottom])

  useEffect(() => {
    if (!hasMessages) {
      return
    }
    scrollToBottom(true)
  }, [activeHistoryIndex, hasMessages, scrollToBottom])

  useEffect(() => {
    if (!shouldUseMobileOverlay) {
      previousChatSnapPointRef.current = chatSnapPoint
      return
    }
    const previousSnapPoint = previousChatSnapPointRef.current
    if (chatSnapPoint === 'expanded' && previousSnapPoint !== 'expanded') {
      const { hasUnread, targetId, latestMessageId } = resolveUnreadAnchorId()
      let cancelled = false
      let rafId: number | undefined
      let innerTimeoutId: ReturnType<typeof setTimeout> | undefined

      const tryScrollToTarget = (attempt: number) => {
        if (cancelled) {
          return
        }
        const didScroll = (() => {
          if (!shouldUseMobileOverlay) {
            return true
          }
          if (hasUnread && targetId) {
            return scrollToMessage(targetId)
          }
          const wrapper = messagesWrapperRef.current
          if (wrapper) {
            const generation = beginProgrammaticScroll()
            wrapper.scrollTo({
              top: wrapper.scrollHeight - wrapper.clientHeight,
              behavior: 'smooth',
            })
            innerTimeoutId = setTimeout(() => {
              endProgrammaticScroll(generation)
            }, SCROLL_FLAG_RESET_DELAY_MS)
          }
          return true
        })()

        if (didScroll || attempt >= 5) {
          if (latestMessageId) {
            setLastReadMessageId(latestMessageId)
          }
          return
        }
        rafId = requestAnimationFrame(() => tryScrollToTarget(attempt + 1))
      }

      rafId = requestAnimationFrame(() => tryScrollToTarget(0))

      previousChatSnapPointRef.current = chatSnapPoint
      return () => {
        cancelled = true
        if (rafId !== undefined) {
          cancelAnimationFrame(rafId)
        }
        clearTimeout(innerTimeoutId)
      }
    }
    previousChatSnapPointRef.current = chatSnapPoint
  }, [
    beginProgrammaticScroll,
    chatSnapPoint,
    endProgrammaticScroll,
    messagesWrapperRef,
    resolveUnreadAnchorId,
    scrollToMessage,
    setLastReadMessageId,
    shouldUseMobileOverlay,
  ])

  useEffect(() => {
    if (!shouldUseMobileOverlay || chatSnapPoint !== 'expanded') {
      return
    }
    if (checkIsNearBottom()) {
      markLatestMessageAsRead()
    }
  }, [chatSnapPoint, checkIsNearBottom, markLatestMessageAsRead, messages, shouldUseMobileOverlay])

  useEffect(() => {
    if (isViewingHistory || isCollapsedMobile || isSmoothScrollingRef.current) {
      setHasContentOutOfView(false)
      return
    }
    const rafId = requestAnimationFrame(() => {
      if (!isSmoothScrollingRef.current) {
        setHasContentOutOfView(checkHasContentOutOfView())
        setIsAtBottom(checkIsNearBottom())
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [messages, isStreaming, isViewingHistory, isCollapsedMobile, checkHasContentOutOfView, checkIsNearBottom])

  // Shrink pin blank by reply growth delta (no scrollTop writes — avoids flicker).
  useEffect(() => {
    if (!isStreaming || !followTail) {
      return
    }
    recomputeActivePinSpacer()
  }, [followTail, isStreaming, messages, recomputeActivePinSpacer])

  // Follow growing content only after the reader explicitly releases the turn pin.
  useLayoutEffect(() => {
    if (!isStreaming || !followTail || isViewingHistory || isPinSessionActiveRef.current) {
      return
    }

    const wrapper = messagesWrapperRef.current
    if (!wrapper || typeof ResizeObserver === 'undefined') {
      return
    }

    const stickIfFollowing = () => {
      if (
        !followTailRef.current
        || !isStreamingRef.current
        || isUserGesturingRef.current
      ) {
        return
      }
      if (isPinSessionActiveRef.current) {
        return
      }
      stickToContentBottom()
    }

    stickIfFollowing()
    const observer = new ResizeObserver(stickIfFollowing)
    wrapper.querySelectorAll<HTMLElement>('[data-message-id]').forEach((node) => {
      observer.observe(node)
    })
    const spacer = wrapper.querySelector<HTMLElement>('[data-spacer]')
    if (spacer) {
      observer.observe(spacer)
    }

    return () => observer.disconnect()
  }, [followTail, isStreaming, isViewingHistory, messages, messagesWrapperRef, stickToContentBottom])

  const scrollToNewMessage = useCallback((messageId: string) => {
    setFollowTail(true)

    const attemptScroll = (retryCount = 0) => {
      const wrapper = messagesWrapperRef.current
      const userMessageEl = wrapper ? qsById(wrapper, messageId) : null

      if (wrapper && userMessageEl) {
        // Reserve the Thinking/loader slot up front so the loader appearing
        // does not cut pin blank; shrink only once real reply content starts.
        const spacerHeight = calculateStreamingPinSpacerHeight({
          viewportHeight: wrapper.clientHeight,
          userMessageHeight: userMessageEl.offsetHeight,
          assistantMessageHeight: 0,
          reservedSpacePx: WRAPPER_VERTICAL_PADDING + LOADING_INDICATOR_HEIGHT,
        })
        isPinSessionActiveRef.current = true
        pinnedReplyContentHeightRef.current = 0
        setDynamicSpacerHeight(spacerHeight)
        dynamicSpacerHeightRef.current = spacerHeight

        // Smooth slide-to-top on send (product polish). While animating,
        // isSmoothScrollingRef is set so stream stick/layout noise cannot fight it;
        // a real user gesture still cancels via cancelSmoothScroll.
        requestAnimationFrame(() => {
          const scrolled = scrollToMessage(messageId, 'smooth')
          if (!scrolled && retryCount < MAX_SCROLL_RETRIES) {
            requestAnimationFrame(() => attemptScroll(retryCount + 1))
          }
        })
      } else if (retryCount < MAX_ELEMENT_FIND_RETRIES) {
        requestAnimationFrame(() => attemptScroll(retryCount + 1))
      } else {
        isPinSessionActiveRef.current = true
        pinnedReplyContentHeightRef.current = 0
        setDynamicSpacerHeight(window.innerHeight * FALLBACK_SPACER_RATIO)
        scrollToMessage(messageId, 'smooth')
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        attemptScroll()
      })
    })
  }, [messagesWrapperRef, scrollToMessage])

  // Wait for the settled streaming state before removing the spacer. Clearing it
  // inside the connection's STREAM_END callback could race the final message render.
  const recalculateSpacerAfterStream = useCallback(() => {}, [])

  useLayoutEffect(() => {
    const streamEnded = wasStreamingRef.current && !isStreaming
    wasStreamingRef.current = isStreaming
    if (streamEnded) {
      collapsePinSession()
    }
  }, [collapsePinSession, isStreaming])

  // Viewport resize: recompute only while an active streaming pin is shrinking.
  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper || typeof ResizeObserver === 'undefined') return

    let debounceTimer: ReturnType<typeof setTimeout> | undefined = undefined
    let previousClientHeight = wrapper.clientHeight
    const observer = new ResizeObserver(() => {
      const shrunkBy = previousClientHeight - wrapper.clientHeight
      previousClientHeight = wrapper.clientHeight
      // Shrinking keeps scrollTop, so something growing under an idle chat (e.g. a prompt above the composer) would crop the latest reply.
      if (shrunkBy > 0 && !isStreamingRef.current && !isPinSessionActiveRef.current) {
        const metrics = readScrollMetrics(wrapper, getSpacerHeightFromDom(wrapper))
        if (isNearContentBottom({ ...metrics, clientHeight: metrics.clientHeight + shrunkBy })) {
          stickToContentBottom()
        }
      }

      if (debounceTimer !== undefined) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined
        if (isPinSessionActiveRef.current && followTailRef.current && isStreamingRef.current) {
          recomputeActivePinSpacer()
        }
      }, SPACER_RECALCULATE_DELAY_MS)
    })
    observer.observe(wrapper)
    return () => {
      observer.disconnect()
      if (debounceTimer !== undefined) clearTimeout(debounceTimer)
    }
  }, [getSpacerHeightFromDom, messagesWrapperRef, recomputeActivePinSpacer, stickToContentBottom])

  return useMemo(() => ({
    handleScroll,
    scrollToBottom,
    isAtBottom,
    followTail,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    scrollToMessage,
    calculateSpacerHeight,
    dynamicSpacerHeight,
    setDynamicSpacerHeight,
    scrollToNewMessage,
    recalculateSpacerAfterStream,
  }), [
    handleScroll,
    scrollToBottom,
    isAtBottom,
    followTail,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    scrollToMessage,
    calculateSpacerHeight,
    dynamicSpacerHeight,
    scrollToNewMessage,
    recalculateSpacerAfterStream,
  ])
}
